<template>
  <div class="flex flex-col w-full flex-1" style="padding-bottom: 50px;">
    <!-- No Search Results State -->
    <div v-if="search.length && !getSearchedFolders?.length" style="height: 400px"
    class="flex-grow w-full flex flex-col items-center justify-center">
      <div class="flex flex-col items-center justify-center">
        <NoSearchResultsIcon/>
        <BaseText type="label" size="sm" class="text-white mt-4">
          No results found
        </BaseText>
        <BaseText type="body" size="sm" class="text-neutral-alpha-650 mt-1">
          Try refining your search
        </BaseText>
      </div>
    </div>
    <!-- Folders -->
    <div v-else v-for="(folder, index) in getSearchedFolders" :key="`folder-${index}`">
      <div class="relative">
        <button class="group w-full flex items-center gap-2 pl-1.5 py-1.5 pr-2.5 rounded-md h-8 flex-shrink-0 text-left select-none
        transition-colors duration-100 hover:bg-neutral-alpha-50 text-neutral-alpha-650 hover:text-neutral-alpha-800 min-w-0"
        :class="{'mt-2': index !== 0, 'text-neutral-alpha-800': openFolders.includes(folder.id), 'text-neutral-alpha-800 bg-neutral-alpha-50': expandedDropdown === folder.id}" 
        @click="toggleFolderOpen(folder.id)" @mouseenter="showNameTooltip(folder.name, `elm-${folder.id}`, true)" @mouseleave="hideNameTooltip"
        :ref="`elm-${folder.id}`">
          <FolderIcon :is-active="getOpenFolders.includes(folder.id)" class="flex-shrink-0"/>
          <BaseText type="body" size="sm" class="item-name flex-grow truncate">
            {{ getFolderName(folder) }}
          </BaseText>
          <!-- Dropdown Opener Button -->
          <button v-if="folder.name !== 'Default'" class="ml-auto flex items-center justify-center w-5 h-5 transition duration-100"
          :class="expandedDropdown === folder.id ? 'text-white' : 'text-neutral-alpha-600 hover:text-white opacity-0 group-hover:opacity-100'" 
          @click.stop.prevent="toggleDropdown(folder.id, 'folder', `elm-${folder.id}`)">
            <MoreIcon />
          </button>
        </button>
        <!-- Options Dropdown -->
        <transition name="folder-dropdown">
          <div v-if="expandedDropdown === folder.id" class="item-dropdown" :class="dropdownPosition" v-on-clickaway="() => { expandedDropdown = null }">
            <SidebarItemDropdown :items="getFolderDropdownItems(folder)" @close="expandedDropdown = null" />
          </div>
        </transition>
      </div>
      <!-- Reports -->
      <div v-if="getOpenFolders.includes(folder.id)" class="pl-3">
        <div class="border-l border-white border-opacity-10 pl-2">
          <div v-for="(report, reportIndex) in folder.reports" :key="`${folder.id}-${report.id}-${reportIndex}`" class="relative"
          @mouseenter="showNameTooltip(report.name, `elm-${folder.id}-${report.id}`, false)" @mouseleave="hideNameTooltip">
            <router-link :to="{ name: 'LensReport', params: { lensId: currentLens?.id, reportId: report?.id } }" class="group"
            :ref="`elm-${folder.id}-${report.id}`">
              <div class="flex items-center mt-0.5 cursor-pointer pl-1 pr-2 rounded-md transition-colors duration-100 h-7 hover:bg-neutral-alpha-25"
              :class="{'mt-0 pt-0': reportIndex === 0, 'bg-neutral-alpha-25': getIsReportActive(report?.id)}">
                <component :is="report?.type === 'comparative' ? 'ComparativeIcon' : 'ReportIcon'" class="text-neutral-alpha-500 transition-colors duration-100 group-hover:text-neutral-alpha-800 flex-shrink-0"
                :class="{'text-neutral-alpha-800': getIsReportActive(report?.id)}" />
                <BaseText type="body" size="sm" class="item-name align-middle ml-1 truncate select-none text-neutral-alpha-600 transition-colors duration-100 group-hover:text-neutral-alpha-800"
                :class="{'text-neutral-alpha-800': getIsReportActive(report?.id)}">
                  {{ report?.name || 'Untitled Report' }}
                </BaseText>
                <!-- Dropdown Opener Button -->
                <button class="ml-auto flex items-center justify-center w-5 h-5 transition-opacity duration-100 opacity-0 group-hover:opacity-100"
                :class="[
                  {'opacity-100': getIsReportActive(report?.id)}, 
                  expandedDropdown === `${folder.id === 'starred' ? 'starred-' : ''}${report.id}` ? 'text-white' : 'text-neutral-alpha-600 hover:text-white'
                ]" 
                @click.stop.prevent="toggleDropdown(report.id, 'report', `elm-${folder.id}-${report.id}`)">
                  <MoreIcon />
                </button>
              </div>
            </router-link>
            <!-- Options Dropdown -->
            <transition name="report-dropdown">
              <div v-if="expandedDropdown === report.id" class="item-dropdown" :class="dropdownPosition" v-on-clickaway="() => { expandedDropdown = null }">
                <SidebarItemDropdown :items="getReportDropdownItems(report, folder)" @close="expandedDropdown = null" />
              </div>
            </transition>
          </div>
          <!-- Create New Report Button -->
          <button class="w-full group flex items-center gap-1 p-1 mt-0.5 rounded-md transition-colors duration-100 hover:bg-neutral-alpha-25"
          @click="openCreateReportModal(folder)">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"
            class="text-neutral-alpha-500 transition-colors duration-100 group-hover:text-neutral-alpha-800">
              <path d="M10 6.25V10M10 10V13.75M10 10H6.25M10 10H13.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <BaseText type="body" size="sm" class="text-neutral-alpha-600 transition-colors duration-100 group-hover:text-neutral-alpha-800">
              Create Report
            </BaseText>
          </button>
        </div>
      </div>
    </div>

    <!-- Name Tooltips -->
    <portal to="name-tooltips">
      <div class="name-tooltip z-50 px-2.5 py-1 rounded-md bg-neutral-malpha-300 transition-opacity duration-75 pointer-events-none"
      :class="nameTooltipData.name ? 'opacity-100' : 'opacity-0'"
      :style="{ top: `${nameTooltipData.top}px`, left: `${nameTooltipData.left}px` }">
        <BaseText type="label" size="sm" class="text-white">
          {{ nameTooltipData.name }}
        </BaseText>
      </div>
    </portal>

    <!-- MODALS -->
    <portal to="folder-modals">
      <FolderModal
        v-if="folderModalOpen"
        key="lens-folder-modal"
        :lens="currentLens"
        @close="$emit('update:folderModalOpen', false)"
      />
    </portal>
    <portal to="folder-modals">
      <ManageFolderModal 
        v-if="showManageFolderModal"
        key="lens-folder-manage-modal"
        :folder="folderToManage"
        :lens="currentLens"
        @close="showManageFolderModal = false"
      />
    </portal>
    <portal to="folder-modals">
      <ConfirmDeleteModal
        v-if="showConfirmDeleteFolderModal"
        key="lens-folder-delete-modal"
        folder
        lens
        :loading="loadingDelete"
        @confirm="handleDeleteFolder(folderToManage)"
        @close="showConfirmDeleteFolderModal = false"
      />
    </portal>
    <portal to="folder-modals">
      <EditReportModal
        v-if="showEditReportModal"
        key="lens-report-edit-modal"
        :report-id="reportToManage?.id"
        :report-name="reportToManage?.name"
        :report-desc="reportToManage?.description"
        @close="showEditReportModal = false"
        @update="handleUpdateLocalReport(reportToManage?.id, $event)"
      />
    </portal>
    <portal to="folder-modals">
      <ChangeFolderModal
        v-if="showMoveReportModal"
        key="lens-report-move-modal"
        :item="reportToManage"
        item-type="report"
        :lens-id="currentLens?.id"
        :init-lens-folder-id="folderToManage?.id"
        @close="showMoveReportModal = false"
      />
    </portal>
    <portal to="folder-modals">
      <ConfirmDeleteModal 
        v-if="showConfirmDeleteReportModal"
        key="lens-report-delete-modal"
        lens
        report
        :loading="loadingDelete"
        @confirm="handleDeleteReport(reportToManage)"
        @close="showConfirmDeleteReportModal = false"
      />
    </portal>
    <portal to="folder-modals">
      <DuplicateReportModal
        v-if="showCopyReportModal"
        :report="reportToCopy"
        :should-fetch="true"
        @close="showCopyReportModal = false"
      />
      <DuplicateFolderModal
        v-if="showDuplicateFolderModal"
        :folder="folderToManage"
        @close="showDuplicateFolderModal = false"
      />
    </portal>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import LensAPI from '@/api/lens'

// Components
import SidebarItemDropdown from './SidebarItemDropdown.vue'
import FolderModal from './FolderModal.vue'
import ManageFolderModal from './ManageFolderModal.vue'
import ConfirmDeleteModal from '../globals/ConfirmDeleteModal.vue'
import EditReportModal from '../lens/report/EditReportModal.vue'
import ChangeFolderModal from '../globals/ChangeFolderModal.vue'
import DuplicateReportModal from '../lens/report/DuplicateReportModal.vue'
import DuplicateFolderModal from '../lens/DuplicateFolderModal.vue'

// Icons
import FolderIcon from '../globals/Icons/SidebarIcons/FolderIcon.vue'
import ReportIcon from '../globals/Icons/SidebarIcons/ReportIcon.vue'
import ComparativeIcon from '../globals/Icons/SidebarIcons/ComparativeIcon.vue'
import NoSearchResultsIcon from '../globals/Icons/SidebarIcons/NoSearchResultsIcon.vue'
import MoreIcon from '../globals/Icons/SidebarIcons/MoreIcon.vue'
import * as DropdownIcons from '../globals/Icons/SidebarIcons/dropdowns'

const DROPDOWN_HEIGHTS = {
  folder: 80,
  report: 153
}

const NAME_TOOLTIP_DELAY = 1000

export default {
  name: 'LensFolders',
  mixins: [clickaway],
  components: {
    FolderModal,
    ManageFolderModal,
    ConfirmDeleteModal,
    EditReportModal,
    ChangeFolderModal,
    DuplicateReportModal,
    DuplicateFolderModal,
    SidebarItemDropdown,
    FolderIcon,
    ReportIcon,
    ComparativeIcon,
    NoSearchResultsIcon,
    MoreIcon
  },
  props: {
    currentLens: {
      type: Object,
      required: true
    },
    folders: {
      type: Array,
      default: () => []
    },
    search: {
      type: String,
      default: ''
    },
    folderModalOpen: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      openFolders: [],
      expandedDropdown: null,
      dropdownPosition: 'bottom',
      showManageFolderModal: false,
      folderToManage: {},
      showConfirmDeleteFolderModal: false,
      showEditReportModal: false,
      showMoveReportModal: false,
      showDuplicateFolderModal: false,
      showConfirmDeleteReportModal: false,
      showCopyReportModal: false,
      reportToCopy: {},
      reportToManage: {},
      loadingDelete: false,

      // Name tooltip
      nameTooltipRef: null,
      nameTooltipData: { name: null, top: 0, left: 0 },
      nameTooltipTimeout: null,
    }
  },
  created () {
    const localOpenFolders = localStorage.getItem(`lens-${this.currentLens?.id}-open-folders`)
    if (localOpenFolders) {
      this.openFolders = JSON.parse(localOpenFolders)
    } else {
      this.openFolders = [this.folders.find(folder => folder.name === 'Default')?.id]
    }
  },
  computed: {
    ...mapGetters('MiscModule', ['getIsPrivateMode']),
    getSearchedFolders () {
      if (!this.search.length) {
        // Sort so that default folder is first
        const defaultFolder = this.folders.find(folder => folder.name === 'Default')
        const otherFolders = this.folders.filter(folder => folder.name !== 'Default')
        return [defaultFolder, ...otherFolders]
      }

      return this.folders.map(folder => {
        const folderMatch = folder.name.toLowerCase().search(this.search.toLowerCase()) !== -1
        if (folderMatch) return folder
        const reportsMatch = folder.reports.filter(report => 
          report.name.toLowerCase().search(this.search.toLowerCase()) !== -1
        )
        if (folderMatch || reportsMatch.length) {
          return {
            ...folder,
            reports: reportsMatch
          }
        }
        return null
      }).filter(Boolean)
    },
    getOpenFolders () {
      if (!this.search) {
        return this.openFolders
      } else {
        return this.getSearchedFolders.map(folder => folder.id)
      }
    }
  },
  methods: {
    ...mapMutations('LensModule', ['SET_SHOW_REPORT_CREATION_MODAL', 'SET_SHOW_REPORT_SHARE_MODAL', 'SET_PENDING_LOCAL_REPORT_UPDATE']),
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    toggleFolderOpen (folderId) {
      if (this.openFolders.includes(folderId)) {
        this.openFolders = this.openFolders.filter((fId) => fId !== folderId)
      } else {
        this.openFolders.push(folderId)
      }

      localStorage.setItem(`lens-${this.currentLens?.id}-open-folders`, JSON.stringify([...this.openFolders]))
    },
    getFolderName (folder) {
      if (this.getIsPrivateMode && folder.name !== 'Default') {
        return '•••••••••••'
      }
      if (folder.name === 'Default') return 'Custom Reports'
      return folder.name || 'Untitled Folder'
    },
    openCreateReportModal (folder) {
      const onLensDashboardRoute = this.$route?.meta?.product === 'lens' && this.$route?.meta?.tab?.length
      if (!onLensDashboardRoute) {
        this.$router.push({ name: 'LensReports', params: { lensId: this.currentLens?.id } }).catch(err => {
          console.log('Error pushing to LensReports', err)
        })
      }
      this.SET_SHOW_REPORT_CREATION_MODAL(folder.id)
    },
    openManageFolderModal (folder) {
      this.folderToManage = folder
      this.showManageFolderModal = true
    },
    openDuplicateFolderModal (folder) {
      this.folderToManage = folder
      this.showDuplicateFolderModal = true
    },
    openConfirmDeleteFolderModal (folder) {
      this.folderToManage = folder
      this.showConfirmDeleteFolderModal = true
    },
    openShareReportModal (report) {
      this.$router.push({ name: 'LensReport', params: { lensId: this.currentLens?.id, reportId: report?.id } }).catch(err => {
        console.log('Error pushing to LensReport', err)
      })
      this.SET_SHOW_REPORT_SHARE_MODAL(true)
    },
    openEditReportModal (report) {
      this.reportToManage = report
      this.showEditReportModal = true
    },
    openMoveReportModal (report, folder) {
      this.reportToManage = report
      this.folderToManage = folder
      this.showMoveReportModal = true
    },
    openConfirmDeleteReportModal (report) {
      this.reportToManage = report
      this.showConfirmDeleteReportModal = true
    },
    openCopyReportModal (report) {
      this.reportToCopy = report
      this.showCopyReportModal = true
    },
    toggleDropdown (id, type, elmRef) {
      if (this.expandedDropdown === id) {
        this.expandedDropdown = null
        return
      }

      // Compute the dropdown position (if there is not enough space below, we render above instead)
      const dropdownHeight = DROPDOWN_HEIGHTS[type]
      const sourcePosition = this.$refs[elmRef]?.[0]?.$el?.getBoundingClientRect()
      let position = 'bottom'
      if (sourcePosition) {
        const availableSpaceBelow = window.innerHeight - sourcePosition.top - sourcePosition.height - 56 // -56px for footer, padding, margin
        position = availableSpaceBelow < dropdownHeight ? 'top' : 'bottom'
      }

      this.dropdownPosition = position
      this.$nextTick(() => { this.expandedDropdown = id })
    },
    getIsReportActive (reportId) {
      return (this.$route.name === 'LensReport' && this.$route.params.reportId === reportId) || this.expandedDropdown === reportId
    },
    getFolderDropdownItems (folder) {
      return [
        { name: 'Edit', action: () => this.openManageFolderModal(folder), icon: DropdownIcons.EditItemIcon },
        { name: 'Duplicate', action: () => this.openDuplicateFolderModal(folder), icon: DropdownIcons.DuplicateItemIcon },
        { name: 'divider' },
        { name: 'Delete', action: () => this.openConfirmDeleteFolderModal(folder), icon: DropdownIcons.DeleteItemIcon }
      ]
    },
    getReportDropdownItems (report, folder) {
      return [
        { name: 'Share', action: () => this.openShareReportModal(report), icon: DropdownIcons.ShareItemIcon },
        { name: 'divider' },
        { name: 'Edit', action: () => this.openEditReportModal(report), icon: DropdownIcons.EditItemIcon },
        { name: 'Duplicate', action: () => this.openCopyReportModal(report), icon: DropdownIcons.DuplicateItemIcon },
        { name: 'Move', action: () => this.openMoveReportModal(report, folder), icon: DropdownIcons.MoveItemIcon },
        { name: 'divider' },
        { name: 'Delete', action: () => this.openConfirmDeleteReportModal(report), icon: DropdownIcons.DeleteItemIcon }
      ]
    },
    async handleDeleteFolder (folder) {
      this.loadingDelete = true
      try {
        await LensAPI.LensFolder.remove(folder.id)
        await this.fetchSidebarLenses()
        this.$showAlert({ type: 'success', message: 'Folder deleted successfully' })
      } catch (error) {
        console.error('Error deleting lens folder:', error)
        this.$showAlert({ type: 'error', message: 'Folder deletion failed. See console for details' })
      } finally {
        this.showConfirmDeleteFolderModal = false
        this.loadingDelete = false
      }
    },
    async handleDeleteReport (report) {
      this.loadingDelete = true
      try {
        await LensAPI.LensReport.remove(report.id)
        await this.fetchSidebarLenses()
        this.$showAlert({ type: 'success', message: 'Report deleted successfully' })
        if (this.$route.params.reportId === report.id) {
          this.$router.push({ name: 'LensDashboardView', params: { lensId: this.currentLens?.id } }).catch(err => {
            console.log('Error pushing to LensDashboardView', err)
          })
        } else {
          this.SET_PENDING_LOCAL_REPORT_UPDATE({ id: report.id, isDeleted: true })
        }
      } catch (error) {
        console.error('Error deleting lens report:', error)
        this.$showAlert({ type: 'error', message: 'Report deletion failed. See console for details' })
      } finally {
        this.loadingDelete = false
        this.showConfirmDeleteReportModal = false
      }
    },
    handleUpdateLocalReport (reportId, toUpdate) {
      this.SET_PENDING_LOCAL_REPORT_UPDATE({ id: reportId, data: toUpdate })
    },
    showNameTooltip (name, refId, isFolder = false) {
      this.nameTooltipRef = refId
      this.nameTooltipTimeout = setTimeout(() => {
        if (this.nameTooltipRef !== refId) return // Safety check

        // Get the elements
        const refElement = this.$refs[refId]?.[0]
        const elm = refElement?.$el || refElement
        const textElm = elm?.querySelector('.item-name')
        if (!elm || !textElm || textElm.offsetWidth < (isFolder ? 175 : 170)) return // Don't show if text doesn't truncate

        // Compute the tooltip position
        const targetRect = elm.getBoundingClientRect()
        const nameStr = name.charAt(0) === '#' ? name.slice(1) : name
        this.nameTooltipData = {
          name: nameStr,
          top: targetRect.top,
          left: targetRect.left
        }
      }, NAME_TOOLTIP_DELAY)
    },
    hideNameTooltip () {
      if (this.nameTooltipTimeout) {
        clearTimeout(this.nameTooltipTimeout)
      }
      this.nameTooltipRef = null
      this.nameTooltipData = { name: null, top: 0, left: 0 }
    }
  }
}
</script>

<style scoped>
.item-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1000;
  border-radius: 4px;
  overflow: hidden;
}
.item-dropdown.top {
  bottom: calc(100% + 4px);
}
.item-dropdown.bottom {
  top: calc(100% + 4px);
}

.name-tooltip {
  position: absolute;
  transform: translate(0, calc(-100% - 6px));
  backdrop-filter: blur(64px);
}

.folder-dropdown-enter-active {
  animation: folderDropdownEnter 100ms ease-in-out forwards;
}
.folder-dropdown-leave-active {
  animation: folderDropdownLeave 100ms ease-in-out forwards;
}
@keyframes folderDropdownEnter {
  0% { opacity: 0; height: 20px; }
  100% { opacity: 1; height: 80px; }
}
@keyframes folderDropdownLeave {
  0% { opacity: 1; height: 80px; }
  100% { opacity: 0; height: 20px; }
}
.report-dropdown-enter-active {
  animation: reportDropdownEnter 100ms ease-in-out forwards;
}
.report-dropdown-leave-active {
  animation: reportDropdownLeave 100ms ease-in-out forwards;
}
@keyframes reportDropdownEnter {
  0% { opacity: 0; height: 20px; }
  100% { opacity: 1; height: 153px; }
}
@keyframes reportDropdownLeave {
  0% { opacity: 1; height: 153px; }
  100% { opacity: 0; height: 20px; }
}
</style>